import { LocationMarkerIcon } from "@heroicons/react/outline";
import {
  compact,
  every,
  find,
  isEmpty,
  keys,
  orderBy,
  toLower,
  values,
  words
} from "lodash";
import moment from "moment";
import { stringifyUrl } from "query-string";
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import FeatureExplanation from "../../../components/FeatureExplanation";
import FilterLabels from "../../../components/labels/FilterLabels";
import Labels from "../../../components/labels/Labels";
import StatusDot from "../../../components/StatusDot";
import { useAuthentication } from "../../../context/authentication";
import { DottedMenu } from "../../../context/dropdownMenu/DropdownMenuProvider";
import { getSmartLabelFromKey } from "../../../context/filteredList/FilteredListProvider";
import { useHotspots } from "../../../context/hotspots";
import { useInstalls } from "../../../context/installs";
import { useLabels } from "../../../context/labels";
import { useLoading } from "../../../context/loading";
import { LoadingKey } from "../../../context/loading/LoadingProvider";
import { useLocations } from "../../../context/locations";
import { useMembers } from "../../../context/members";
import { useWorkspaces } from "../../../context/workspaces";
import { avatarsApi, Entity } from "../../../utils/constants";
import { downloadCsv } from "../../../utils/csv";
import {
  EXPORT_DATETIME_FORMAT,
  EXPORT_DATE_FORMAT,
  formatDateToLocale
} from "../../../utils/dates";
import {
  formatHotspotName,
  formatHotspotPlace
} from "../../../utils/hotspotUtils";
import { truncateText } from "../../../utils/text";
import { Install, PlanType } from "../../../utils/types";
import useDebounce from "../../../utils/useDebounce";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../utils/useFilteredLabels";
import EntityDataTable from "../common/EntityDataTable";
import MemberCell from "../common/MemberCell";
import TablePropertiesSidebar from "../common/TablePropertiesSidebar";
import TableSearchInput from "../common/TableSearchInput";
import useDataTable, { Property } from "../common/useDataTable";
import { PaginationKey } from "../common/usePagination";
import LockedWorkspace from "../LockedWorkspace";
import LockedWorkspaceByCommunityPlan from "../LockedWorkspaceByCommunityPlan";
import LockedWorkspaceByMembers from "../LockedWorkspaceByMembers";
import InstallActionsButton from "./InstallActionsButton";
import { isActiveInstall } from "./utils";

interface Props {}

const InstallsData: React.FC<Props> = () => {
  const { wrapActionWithAuth } = useAuthentication();
  const { hotspotsMap } = useHotspots();
  const {
    currentWorkspace,
    currentWorkspaceId,
    workspaces,
    currentWorkspaceIsLocked,
    currentWorkspaceAllowedForCommunityPlan
  } = useWorkspaces();
  const { locations } = useLocations();
  const { members } = useMembers();
  const { installs, toggleInstallSidebar } = useInstalls();
  const { labels, linkedLabels } = useLabels();
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);
  const [showPropertiesSidebar, setShowPropertiesSidebar] = useState(false);
  const sortOrder = useDataTable((state) => state.sortOrder[Entity.install]);
  const searchInput = useDataTable(
    (state) => state.searchInput[Entity.install]
  );
  const { membersLimitExceeded } = useMembers();
  const debouncedSearchInput = useDebounce(searchInput, 200);

  const { getIsReady } = useLoading();

  const smartLabelFilters: {
    [key: string]: (install: Install) => boolean;
  } = useMemo(
    () => ({
      "active install": (install) => isActiveInstall(install),
      "future install": ({ installed_at }) => !installed_at,
      "past install": (install) =>
        !!install.installed_at && !isActiveInstall(install)
    }),
    []
  );

  const filteredInstallLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.workspaceInstalls],
        labels,
        smartLabelFilters,
        Entity.install
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredInstalls = useMemo(() => {
    let filteredList = values(installs).filter((install) => {
      if (
        !isEmpty(filteredInstallLabels) &&
        !every(filteredInstallLabels, ({ uuid }) => {
          const smartFilterFunction = (smartLabelFilters as any)[uuid]; // TODO: fix typing

          return smartFilterFunction
            ? smartFilterFunction(install)
            : (linkedLabels[install.uuid] || []).includes(uuid);
        })
      ) {
        return false;
      }

      if (debouncedSearchInput) {
        const searchInputWords = words(debouncedSearchInput).map(toLower);

        const hotspot = hotspotsMap[install.hotspot_uuid];
        const location = locations[install.location_uuid];

        return every(searchInputWords, (word) => {
          if (
            hotspot &&
            (hotspot.name?.includes(word) ||
              toLower(formatHotspotPlace(hotspot.place)).includes(word))
          ) {
            return true;
          }

          if (
            location &&
            (toLower(location.name).includes(word) ||
              toLower(location.address || "").includes(word))
          ) {
            return true;
          }

          // TODO: also filter based on contact details for the linked payment agreements

          return false;
        });
      }
      return true;
    });

    if (sortOrder) {
      filteredList = orderBy(
        filteredList,
        [sortOrder.iteratee],
        [sortOrder.order]
      );
    }

    if (currentWorkspace?.active_plan?.type === PlanType.community) {
      return filteredList.slice(-3);
    }

    return filteredList;
  }, [
    installs,
    sortOrder,
    currentWorkspace?.active_plan?.type,
    filteredInstallLabels,
    debouncedSearchInput,
    smartLabelFilters,
    linkedLabels,
    hotspotsMap,
    locations
  ]);

  const properties: { [key: string]: Property<Install> } = useMemo(
    () => ({
      installs: {
        key: "installs",
        title: "Installs",
        style: { minWidth: "18rem" },
        excludeForExport: true,
        renderContent: (install) => {
          const hotspot = hotspotsMap[install.hotspot_uuid];
          const location = locations[install.location_uuid];

          return (
            <Link
              className="relative flex items-center space-x-3 group text-sm font-medium text-gray-900 dark:text-white"
              to={`/installs/${install.uuid}/info`}
            >
              <div className="flex-shrink-0">
                <span className="inline-block relative">
                  <img
                    className="h-10 w-10 rounded-full"
                    src={stringifyUrl({
                      url: `${avatarsApi}/bottts/${install.hotspot_uuid}.svg`,
                      query: { w: 40, h: 40 }
                    })}
                    alt="Hotspot avatar"
                  />
                  {hotspot && (
                    <StatusDot
                      hotspot={hotspot}
                      withoutCustomRewardScaling
                      className="absolute top-0 right-0 block"
                    />
                  )}
                </span>
              </div>
              <div className="flex-1 min-w-0 focus:outline-none">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                  {hotspot
                    ? formatHotspotName(hotspot)
                    : truncateText(install.hotspot_uuid, 20)}
                </p>
                <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                  {formatHotspotPlace(location?.name)}
                </p>
              </div>
            </Link>
          );
        }
      },
      hotspot: {
        key: "hotspot",
        title: "Hotspot",
        style: { minWidth: "18rem" },
        renderForExport: (install) => {
          const hotspot = hotspotsMap[install.hotspot_uuid];

          return formatHotspotName(hotspot);
        },
        renderContent: (install) => {
          const hotspot = hotspotsMap[install.hotspot_uuid];

          return (
            <Link
              className="relative flex items-center space-x-3 group text-sm font-medium text-gray-900 dark:text-white"
              to={`/hotspots/${install.hotspot_uuid}/installs`}
            >
              <div className="flex-shrink-0">
                <span className="inline-block relative">
                  <img
                    className="h-10 w-10 rounded-full"
                    src={stringifyUrl({
                      url: `${avatarsApi}/bottts/${install.hotspot_uuid}.svg`,
                      query: { w: 40, h: 40 }
                    })}
                    alt="Hotspot avatar"
                  />
                  {hotspot && (
                    <StatusDot
                      hotspot={hotspot}
                      withoutCustomRewardScaling
                      className="absolute top-0 right-0 block"
                    />
                  )}
                </span>
              </div>
              <div className="flex-1 min-w-0 focus:outline-none">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                  {hotspot
                    ? formatHotspotName(hotspot)
                    : truncateText(install.hotspot_uuid, 20)}
                </p>
                <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                  {formatHotspotPlace(hotspot?.place)}
                </p>
              </div>
            </Link>
          );
        }
      },
      location: {
        key: "location",
        title: "Location",
        style: { minWidth: "18rem" },
        renderForExport: (install) => {
          const location = locations[install.location_uuid];
          return location?.name || null;
        },
        renderContent: (install) => {
          const location = locations[install.location_uuid];

          if (!location) return null;

          return (
            <Link
              className="relative flex items-center space-x-3 group text-sm font-medium text-gray-900 dark:text-white"
              to={`/locations/${location.uuid}/super-simulations`}
            >
              <div className="flex-shrink-0">
                <span className="inline-block relative">
                  <LocationMarkerIcon className="h-6 w-6 text-gray-500 group-hover:text-gray-900 dark:text-gray-300 dark:group-hover:text-white" />
                </span>
              </div>
              <div className="min-w-0 focus:outline-none">
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline truncate">
                  {location.name}
                </p>
                <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                  {formatHotspotPlace(location?.address)}
                </p>
              </div>
            </Link>
          );
        }
      },
      labels: {
        key: "labels",
        title: "Labels",
        style: { minWidth: "26rem" },
        renderForExport: (install) => {
          const installLabelNames = compact(
            (linkedLabels[install.uuid] || []).map((uuid) => labels[uuid]?.name)
          );

          if (isEmpty(installLabelNames)) return null;

          return installLabelNames.join(", ");
        },
        renderContent: (install) => (
          <Labels
            entity={Entity.install}
            linkedUuid={install.uuid}
            filterKey={LabelFilterKey.workspaceInstalls}
          />
        )
      },
      installed_at: {
        key: "installed_at",
        title: "Installed",
        style: { minWidth: "10rem" },
        sortIteratee: "installed_at",
        renderForExport: (install) => {
          const value = install.installed_at;

          if (!value) return null;

          return moment(value).utc().format(EXPORT_DATE_FORMAT);
        },
        renderContent: (install) =>
          install.installed_at && formatDateToLocale(install.installed_at)
      },
      uninstalled_at: {
        key: "uninstalled_at",
        title: "Uninstalled",
        style: { minWidth: "10rem" },
        sortIteratee: "uninstalled_at",
        renderForExport: (install) => {
          const value = install.uninstalled_at;

          if (!value) return null;

          return moment(value).utc().format(EXPORT_DATE_FORMAT);
        },
        renderContent: (install) =>
          install.uninstalled_at && formatDateToLocale(install.uninstalled_at)
      },
      created_at: {
        key: "created_at",
        title: "Added",
        style: { minWidth: "13rem" },
        sortIteratee: "created_at",
        renderForExport: (install) => {
          const member = find(members, { uuid: install.created_by });

          if (!member) return null;

          return `${member?.name} - ${moment(install.created_at)
            .utc()
            .format(EXPORT_DATETIME_FORMAT)}`;
        },
        renderContent: (install) => (
          <MemberCell
            memberUuid={install.created_by}
            subtitle={formatDateToLocale(install.created_at)}
          />
        )
      },
      updated_at: {
        key: "updated_at",
        title: "Updated",
        style: { minWidth: "13rem" },
        sortIteratee: "updated_at",
        renderForExport: (install) => {
          const member = find(members, { uuid: install.updated_by });

          if (!member) return null;

          return `${member.name} - ${moment(install.updated_at)
            .utc()
            .format(EXPORT_DATETIME_FORMAT)}`;
        },
        renderContent: (install) =>
          install.updated_by ? (
            <MemberCell
              memberUuid={install.updated_by}
              subtitle={formatDateToLocale(install.updated_at)}
            />
          ) : (
            "-"
          )
      },
      actions: {
        key: "actions",
        title: "Actions",
        excludeForExport: true,
        renderContent: (install) => (
          <InstallActionsButton
            install={install}
            toggleEditInstallForm={toggleInstallSidebar}
          />
        )
      }
    }),
    [
      hotspotsMap,
      labels,
      linkedLabels,
      locations,
      members,
      toggleInstallSidebar
    ]
  );

  const filteredProperties = useDataTable(
    useCallback(
      (state) =>
        state.orderedProperties[Entity.install]
          .filter((key) =>
            state.visibleProperties[Entity.install].includes(key)
          )
          .map((key) => properties[key]),
      [properties]
    )
  );

  const getCsvData = useCallback(
    () =>
      filteredInstalls.map((install) =>
        filteredProperties
          .filter((property) => !property.excludeForExport)
          .reduce((acc: { [propertyKey: string]: any }, property) => {
            if (typeof property.title !== "string") return acc;

            acc[property.title] = property.renderForExport
              ? property.renderForExport(install)
              : property.renderContent(install);
            return acc;
          }, {})
      ),
    [filteredInstalls, filteredProperties]
  );

  const isLoading = useMemo(
    () => isEmpty(installs) && !getIsReady([LoadingKey.workspaceInstalls]),
    [getIsReady, installs]
  );

  const renderPageContent = useMemo(() => {
    if (currentWorkspaceIsLocked) {
      return (
        <LockedWorkspace
          title="Payment failed"
          paragraphs={[
            "Your subscription payment failed. Please resolve the issue with your payment method."
          ]}
          buttonText="Update payment details"
          buttonLink="/workspaces/settings/billing"
        />
      );
    }

    if (!currentWorkspaceAllowedForCommunityPlan) {
      return <LockedWorkspaceByCommunityPlan />;
    }

    if (membersLimitExceeded) {
      return <LockedWorkspaceByMembers />;
    }

    if (isLoading) {
      return <CenteredLoading className="sm:h-full h-96" />;
    }

    if (isEmpty(installs)) {
      return (
        <FeatureExplanation
          title="Installs"
          paragraphs={installsExplanationParagraphs}
        >
          <button
            id="add-install-button"
            type="button"
            className="mt-6 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
            onClick={wrapActionWithAuth(() => toggleInstallSidebar())}
          >
            Add install
          </button>
        </FeatureExplanation>
      );
    }

    return (
      <>
        <div className="p-4">
          <TableSearchInput
            entity={Entity.install}
            placeholder="Search installs by hotspot or location name"
          />

          <div className="mt-2 flex items-center">
            <FilterLabels
              className="flex-1"
              entity={Entity.install}
              filteredLabels={filteredInstallLabels}
              smartLabels={keys(smartLabelFilters).map((key) =>
                getSmartLabelFromKey(key, Entity.install)
              )}
              filterKey={LabelFilterKey.workspaceInstalls}
            />
            <DottedMenu
              text="Open options"
              menuItems={[
                {
                  text: "Edit columns",
                  onClick: () => {
                    setShowPropertiesSidebar(true);
                  }
                },
                {
                  onClick: wrapActionWithAuth(() => {
                    downloadCsv(
                      getCsvData(),
                      `${
                        workspaces[currentWorkspaceId ?? ""]?.name ?? ""
                      } installs ${moment().utc().format("YYYY_MM_DD_HH_mm")}`
                    );
                  }),
                  text: `Export ${
                    filteredInstalls.length !== values(installs).length
                      ? "filtered"
                      : "all"
                  } installs`,
                  disabled: isEmpty(filteredInstalls)
                }
              ]}
            />

            <button
              id="add-install-button"
              type="button"
              className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
              onClick={wrapActionWithAuth(() => toggleInstallSidebar())}
            >
              Add install
            </button>
          </div>
        </div>

        <EntityDataTable<Install>
          paginationKey={PaginationKey.workspaceInstalls}
          entity={Entity.install}
          items={filteredInstalls}
          properties={properties}
        />
      </>
    );
  }, [
    currentWorkspaceAllowedForCommunityPlan,
    currentWorkspaceId,
    currentWorkspaceIsLocked,
    filteredInstallLabels,
    filteredInstalls,
    getCsvData,
    installs,
    isLoading,
    membersLimitExceeded,
    properties,
    smartLabelFilters,
    toggleInstallSidebar,
    workspaces,
    wrapActionWithAuth
  ]);

  return (
    <>
      <TablePropertiesSidebar<Install>
        entity={Entity.install}
        properties={properties}
        open={showPropertiesSidebar}
        setOpen={setShowPropertiesSidebar}
      />

      {renderPageContent}
    </>
  );
};

export default InstallsData;

//
// Utils
//

export const installsExplanationParagraphs = [
  <>
    Installs track the{" "}
    <Link
      className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
      to="/workspace/location"
    >
      location
    </Link>{" "}
    at which a{" "}
    <Link
      className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
      to="/workspace/hotspots"
    >
      hotspot
    </Link>{" "}
    was installed over what period of time.
  </>,
  <>
    This eases the deployment and{" "}
    <Link
      className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
      to="/workspace/payments"
    >
      payout management
    </Link>{" "}
    of many{" "}
    <Link
      className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
      to="/workspace/hotspots"
    >
      hotspots
    </Link>
    .
  </>
];
